/**
 * Réplique locale de l'état serveur : un snapshot, puis des deltas appliqués
 * dans l'ordre. Utilisée par le client et par les tests de convergence du
 * serveur (réplique reconstruite == état serveur).
 */
import type { DeltaMsg, SnapshotMsg } from './protocol';
import type { AgentWire, Building, ClockWire, Lift, Piste, PlayerPrivate, StationPublic, WorldPublic } from './state';
import type { Groomer } from './grooming';
import { applyUsagePatch, type UsageHistory } from './usage';

export type ReplicaPart = 'clock' | 'world' | 'station' | 'lifts' | 'pistes' | 'buildings' | 'agents' | 'groomers' | 'me' | 'usage';

export class Replica {
  clock: ClockWire;
  world: WorldPublic;
  station: StationPublic;
  lifts = new Map<number, Lift>();
  pistes = new Map<number, Piste>();
  buildings = new Map<number, Building>();
  agents = new Map<number, AgentWire>();
  groomers = new Map<number, Groomer>();
  me: PlayerPrivate | null;
  usage: UsageHistory | null;

  constructor(snap: SnapshotMsg) {
    this.clock = snap.clock;
    this.world = snap.world;
    this.station = snap.station;
    this.me = snap.me;
    this.usage = snap.usage ?? null;
    for (const l of snap.lifts) this.lifts.set(l.id, l);
    for (const p of snap.pistes) this.pistes.set(p.id, p);
    for (const b of snap.buildings) this.buildings.set(b.id, b);
    for (const a of snap.agents) this.agents.set(a.i, a);
    for (const g of snap.groomers ?? []) this.groomers.set(g.id, g);
  }

  /**
   * Applique un delta. Renvoie les parties modifiées, ou null si le delta ne
   * suit pas le dernier tick appliqué (il faut alors demander un resync).
   */
  apply(d: DeltaMsg): Set<ReplicaPart> | null {
    if (d.base !== this.clock.tick) return null;
    const changed = new Set<ReplicaPart>(['clock']);
    this.clock = d.clock;
    if (d.world) {
      this.world = { ...this.world, ...d.world };
      changed.add('world');
    }
    if (d.station) {
      this.station = { ...this.station, ...d.station };
      changed.add('station');
    }
    if (d.me && this.me) {
      this.me = { ...this.me, ...d.me };
      changed.add('me');
    }
    if (d.usage && this.usage) {
      for (const p of d.usage) applyUsagePatch(this.usage, p);
      changed.add('usage');
    }
    if (d.lifts && applyCollection(this.lifts, d.lifts, (e) => e.id)) changed.add('lifts');
    if (d.pistes && applyCollection(this.pistes, d.pistes, (e) => e.id)) changed.add('pistes');
    if (d.buildings && applyCollection(this.buildings, d.buildings, (e) => e.id)) changed.add('buildings');
    if (d.agents && applyCollection(this.agents, d.agents, (e) => e.i)) changed.add('agents');
    if (d.groomers && applyCollection(this.groomers, d.groomers, (e) => e.id)) changed.add('groomers');
    return changed;
  }
}

function applyCollection<T>(map: Map<number, T>, delta: { u?: T[]; r?: number[] }, idOf: (e: T) => number): boolean {
  let any = false;
  if (delta.r) {
    for (const id of delta.r) any = map.delete(id) || any;
  }
  if (delta.u) {
    for (const e of delta.u) {
      map.set(idOf(e), e);
      any = true;
    }
  }
  return any;
}
